import { useMutation, useQueryClient } from "@tanstack/react-query";
import { find } from "lodash/fp";

import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import { UserIntegrationSetting } from "@app/common/types";
import { replaceWhere } from "@app/utils/arrays";

import { getIntegrationSettingsQueryKey } from "./useIntegrations";

const EMPTY_SETTINGS: UserIntegrationSetting[] = [];

const useIntegrationSettings = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const updateIntegrationSettingMutation = useMutation({
    mutationFn: apiClient.integrationSettings.updateIntegrationSetting,
    onMutate: (body) => {
      const userIntegrationSettingsQueryKey = getIntegrationSettingsQueryKey(
        user?.id
      );

      const settingsResponse =
        queryClient.getQueryData<UserIntegrationSetting[]>(
          userIntegrationSettingsQueryKey
        ) ?? EMPTY_SETTINGS;

      const existingSetting = find(
        { integrationKind: body.integrationKind },
        settingsResponse
      );

      if (existingSetting) {
        const updatedSetting = { ...existingSetting, ...body };
        const newSettings = replaceWhere(
          settingsResponse,
          updatedSetting,
          (s) => s.integrationKind === body.integrationKind
        );
        queryClient.setQueryData(userIntegrationSettingsQueryKey, newSettings);
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["integrations"] }),
  });

  return {
    updateIntegrationSetting: updateIntegrationSettingMutation,
  };
};

export default useIntegrationSettings;
